/**
 * pipelineRun · «прочитай прогон CI» (S17 · T04). Per ADR-015.
 * «Запустить» → этапы пайплайна загораются зелёным по очереди; на этапе
 * data-result="fail" останавливается красным, раскрывает его лог и показывает
 * вопрос-диагноз. Этапы после падения — серые «не выполнялись». Детерминированно
 * (состояния заданы в разметке) — taster-тренажёр, не реальный CI.
 *
 * Контракт разметки:
 *   <div class="pipeline-run" data-fin="мораль">
 *     <div class="plr-head"><span class="plr-ttl">…</span>
 *       <button type="button" class="plr-run">Запустить пайплайн</button></div>
 *     <div class="plr-stages">
 *       <div class="plr-stage" data-result="ok">checkout</div>
 *       <div class="plr-stage" data-result="fail" data-log="строка&#10;ещё строка">pytest</div>
 *       <div class="plr-stage" data-result="skip">upload artifacts</div>
 *     </div>
 *     <pre class="plr-log" aria-live="polite"></pre>
 *     <div class="plr-quiz"><div class="plr-q">Что произошло?</div>
 *       <button type="button" class="plr-opt" data-ok="1">верный</button>
 *       <button type="button" class="plr-opt">неверный</button></div>
 *     <div class="plr-verdict" aria-live="polite"></div>
 *   </div>
 *
 * No-JS: этапы видны как список, лог пуст. StrictMode-safe (guard + cleanup
 * восстанавливает innerHTML, таймеры очищаются).
 */
export function initPipelineRun(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  root.querySelectorAll<HTMLElement>('.pipeline-run').forEach((el) => {
    if (el.dataset.plrInit === '1') return;
    el.dataset.plrInit = '1';
    const original = el.innerHTML;

    const runBtn = el.querySelector<HTMLButtonElement>('.plr-run');
    const stages = Array.from(el.querySelectorAll<HTMLElement>('.plr-stage'));
    const logEl = el.querySelector<HTMLElement>('.plr-log');
    const quiz = el.querySelector<HTMLElement>('.plr-quiz');
    const opts = Array.from(el.querySelectorAll<HTMLButtonElement>('.plr-opt'));
    const verdict = el.querySelector<HTMLElement>('.plr-verdict');
    if (!runBtn || stages.length === 0) return;

    el.classList.add('plr-ready');
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    let ran = false;

    const clearTimers = () => {
      timers.forEach((t) => clearTimeout(t));
      timers.length = 0;
    };
    const resetView = () => {
      clearTimers();
      stages.forEach((s) => s.classList.remove('plr-ok', 'plr-fail', 'plr-skip'));
      if (logEl) {
        logEl.textContent = '';
        logEl.classList.remove('plr-show');
      }
      quiz?.classList.remove('plr-show');
      if (verdict) {
        verdict.textContent = '';
        verdict.className = 'plr-verdict';
      }
      opts.forEach((o) => {
        o.classList.remove('plr-pick-ok', 'plr-pick-no');
        o.disabled = false;
      });
    };

    const run = () => {
      if (ran) {
        ran = false;
        resetView();
        runBtn.textContent = 'Запустить пайплайн';
        return;
      }
      ran = true;
      runBtn.textContent = 'Сбросить';
      const failedAt = stages.findIndex((s) => s.dataset.result === 'fail');
      stages.forEach((s, idx) => {
        const t = setTimeout(() => {
          if (failedAt >= 0 && idx > failedAt) {
            s.classList.add('plr-skip');
            return;
          }
          if (idx === failedAt) {
            s.classList.add('plr-fail');
            if (logEl) {
              logEl.textContent = (s.dataset.log ?? '').replace(/\\n/g, '\n');
              logEl.classList.add('plr-show');
            }
            quiz?.classList.add('plr-show');
          } else {
            s.classList.add('plr-ok');
          }
        }, idx * 480);
        timers.push(t);
      });
      if (failedAt < 0) {
        const t = setTimeout(() => {
          if (verdict) {
            verdict.textContent = el.dataset.fin || 'Зелёно — весь набор прошёл.';
            verdict.className = 'plr-verdict plr-ok-msg';
          }
        }, stages.length * 480);
        timers.push(t);
      }
    };

    const onOpt = (e: Event) => {
      const b = e.currentTarget as HTMLButtonElement;
      if (b.dataset.ok === '1') {
        opts.forEach((o) => {
          o.disabled = true;
        });
        b.classList.add('plr-pick-ok');
        if (verdict) {
          verdict.textContent = el.dataset.fin || 'Верно. Красный = адрес «где смотреть», а не повод паниковать.';
          verdict.className = 'plr-verdict plr-ok-msg';
        }
      } else {
        b.classList.add('plr-pick-no');
        b.disabled = true;
        if (verdict) {
          verdict.textContent = 'Не то — перечитай красный шаг в логе выше.';
          verdict.className = 'plr-verdict plr-err-msg';
        }
      }
    };

    runBtn.addEventListener('click', run);
    opts.forEach((o) => o.addEventListener('click', onOpt));

    cleanups.push(() => {
      clearTimers();
      runBtn.removeEventListener('click', run);
      opts.forEach((o) => o.removeEventListener('click', onOpt));
      el.innerHTML = original;
      el.classList.remove('plr-ready');
      delete el.dataset.plrInit;
    });
  });

  return () => cleanups.forEach((fn) => fn());
}
